/**
 * Post-consent redirect resolution for InitValidator.
 *
 * ConsentGate (HLV) sends `?redirect=<absolute /entry/connect/oauth2?stashID=…>`.
 * That URL must be resumed as-is after `initializeCommunity` — not prefixed with
 * HOME_BASE_URL and not wrapped in Khoros oauth2sso_v2.
 *
 * Gated by `community-forums-hlv-vanilla`: the gate is read here (as in
 * `url-generator`) so callers stay unaware of it. OFF resolves through the
 * pre-HLV Khoros flow, ON resolves through the Vanilla flow below.
 */

import { hlvVanillaMigrationIsActive } from '../../services/statsig/featureGates';
import { getAllowedExternalRedirect } from '../../utils/url-generator';

export type ConsentRedirectResolution =
  | { kind: 'vanilla-connect'; url: string }
  | { kind: 'forums'; url: string }
  | { kind: 'home'; url: string };

const VANILLA_CONNECT_PATH = '/entry/connect/oauth2';

/** Exact hosts always trusted for absolute continue URLs. */
const TRUSTED_EXACT_HOSTS = new Set([
  'community.atlassian.com',
  'community.stg.atlassian.com',
  'community.dev.atlassian.com',
]);

function normalizeHomeBaseUrl(homeBaseUrl: string): string {
  return homeBaseUrl.replace(/\/$/, '');
}

/**
 * Whether an absolute redirect host is allowlisted (open-redirect guard).
 * Trusts the HOME_BASE_URL host plus the exact hosts above. Cross-origin HLV
 * URLs are not covered here — they go through `getAllowedExternalRedirect`,
 * which matches the full return URL (host + `/entry/` path).
 */
export function isTrustedRedirectHost(hostname: string, homeBaseUrl: string): boolean {
  const host = hostname.toLowerCase();
  if (!host) {
    return false;
  }

  try {
    const homeHost = new URL(homeBaseUrl).hostname.toLowerCase();
    if (homeHost && host === homeHost) {
      return true;
    }
  } catch {
    // ignore invalid HOME_BASE_URL — fall through to static allowlist
  }

  return TRUSTED_EXACT_HOSTS.has(host);
}

function isVanillaConnectPath(pathname: string): boolean {
  return pathname.includes(VANILLA_CONNECT_PATH);
}

function isForumsPath(pathOrUrl: string): boolean {
  return pathOrUrl.includes('/forums');
}

/**
 * Pre-HLV resolution, used while `community-forums-hlv-vanilla` is OFF.
 *
 * A `/forums` redirect became a Khoros SSO referer of HOME_BASE_URL + the raw
 * param and everything else went to Community Home. The param is concatenated
 * verbatim — no absolute-URL parsing and no Vanilla connect resume — so the
 * legacy flow behaves exactly as it did before the HLV work.
 */
function resolveLegacyConsentRedirect(
  redirectParam: string,
  homeBaseUrl: string,
): ConsentRedirectResolution {
  if (isForumsPath(redirectParam)) {
    return { kind: 'forums', url: `${homeBaseUrl}${redirectParam}` };
  }

  return { kind: 'home', url: `${homeBaseUrl}/` };
}

/**
 * Resolve `?redirect=` after consent accept / when already consented.
 *
 * With `community-forums-hlv-vanilla` OFF, delegates to the legacy Khoros
 * resolution above. With the gate ON:
 *
 * - Absolute allowlisted `/entry/connect/oauth2…` → resume as-is
 * - Relative `/entry/connect/oauth2…` → HOME_BASE_URL + path (same-origin / CF case)
 * - Relative (or absolute trusted) `/forums…` → forums SSO referer URL
 * - Untrusted absolute host → Community Home
 * - Other relative paths → Community Home (preserves pre-HLV behavior)
 */
export function resolveConsentRedirect(
  redirectParam: string | null | undefined,
  homeBaseUrl: string,
): ConsentRedirectResolution {
  const home = `${normalizeHomeBaseUrl(homeBaseUrl)}/`;

  if (!redirectParam) {
    return { kind: 'home', url: home };
  }

  if (!hlvVanillaMigrationIsActive()) {
    return resolveLegacyConsentRedirect(redirectParam, homeBaseUrl);
  }

  const trimmed = redirectParam.trim();
  const looksAbsolute = /^https?:\/\//i.test(trimmed);

  if (looksAbsolute) {
    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      return { kind: 'home', url: home };
    }

    // Pre-CloudFront, HLV lives on its own origin, so the return URL is trusted
    // as a whole (exact host + `/entry/` path) rather than by host alone.
    const isHlvReturnUrl = getAllowedExternalRedirect(trimmed) !== null;

    if (!isHlvReturnUrl && !isTrustedRedirectHost(parsed.hostname, homeBaseUrl)) {
      return { kind: 'home', url: home };
    }

    if (isVanillaConnectPath(parsed.pathname)) {
      return { kind: 'vanilla-connect', url: trimmed };
    }

    if (isForumsPath(parsed.pathname + parsed.search)) {
      return { kind: 'forums', url: trimmed };
    }

    // Trusted absolute but neither connect nor forums — stay on Community Home
    // (same product choice as relative non-forums redirects).
    return { kind: 'home', url: home };
  }

  // Relative path / query
  if (isVanillaConnectPath(trimmed)) {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return { kind: 'vanilla-connect', url: `${normalizeHomeBaseUrl(homeBaseUrl)}${path}` };
  }

  if (isForumsPath(trimmed)) {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return { kind: 'forums', url: `${normalizeHomeBaseUrl(homeBaseUrl)}${path}` };
  }

  return { kind: 'home', url: home };
}
